import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  CheckoutUpsellExtension,
  DynamicReviewsExtension,
  ProductReviews,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { DynamicRule } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { getExtensionRules, PRODUCT_RELATED_RULE_ENTITIES } from '../../../shared/all-extension-rules';
import { sellingPlanValidator } from './validators/selling-plan.validator';

export type ProductsInCartMode = `${CheckoutUpsellExtension.ProductsInCartMode}`;
export type UpsellRecommendedProductsMode = `${CheckoutUpsellExtension.UpsellRecommendedProductsMode}`;
export type HideProductsAlreadyInCart = CheckoutUpsellExtension.HideProductsAlreadyInCart;
export type NoItemsToDisplay = CheckoutUpsellExtension.NoItemsToDisplay;
export type PrioritizeRecommendedItemsBy = `${CheckoutUpsellExtension.PrioritizeRecommendedItemsBy}`;
export type UpsellProduct = CheckoutUpsellExtension.UpsellProduct;
export type UpsellRecommendedProducts = CheckoutUpsellExtension.UpsellRecommendedProducts;
export type ProductsInCart = CheckoutUpsellExtension.ProductsInCart;
export type ProductSelection = CheckoutUpsellExtension.ProductSelection;
export type ProductsInCartRule = DynamicRule;

export type GroupDiscountApplyToMode = `${CheckoutUpsellExtension.GroupDiscountApplyToMode}`;
export type ProductReviewsConfig = CheckoutUpsellExtension.ProductReviewsConfig;
export type GroupDiscountConfigurationValues = Partial<CheckoutUpsellExtension.GroupDiscountConfiguration>;
export type ProductSelectionWithGroupDiscounts = ProductSelection;

export const DEFAULT_RULE_GROUP: Omit<ProductSelection, 'id'> = {
  productsInCart: {
    mode: CheckoutUpsellExtension.ProductsInCartMode.ANY,
    rules: [],
  },
  upsellRecommendedProducts: {
    mode: CheckoutUpsellExtension.UpsellRecommendedProductsMode.SHOPIFY_RECOMMENDATIONS,
    products: [],
    rules: [],
  },
  inStockProductsOnly: true,
  matchFulfillmentCenterToCartItems: false,
  includeDigitalItems: true,
  matchProductVariants: false,
  hideProductsAlreadyInCart: 'parentProduct',
  maxItemsToDisplay: 100,
  NoItemsToDisplay: 'showShopifyRecommendations',
  prioritizeRecommendedItemsBy: CheckoutUpsellExtension.PrioritizeRecommendedItemsBy.HIGH_PRICE,
};

export interface DiscountConfigurationFormGroup {
  discountType: UntypedFormControl;
  discountValue: UntypedFormControl;
  labelText: UntypedFormControl;
  labelColor: UntypedFormControl;
  mode: UntypedFormControl;
  allowedDiscountValues: UntypedFormArray;
  zeroDiscountLabelText: UntypedFormControl;
}

export interface StyleFormGroup {
  displayType: UntypedFormControl;
  buttonAppearance: UntypedFormControl;
  numberOfRowsInRowDisplayType: UntypedFormControl;
  carouselNavigationStyle: UntypedFormControl;
}

export interface AdditionalDisplayTextFormGroup {
  type: UntypedFormControl;
  display: UntypedFormControl;
  text: UntypedFormControl;
  priority: UntypedFormControl;
}

export interface SettingsFormGroup {
  thresholdType: UntypedFormControl;
  textWithThreshold: UntypedFormControl;
  textWithoutThreshold: UntypedFormControl;
  showMessageIfFreeShippingAvailable: UntypedFormControl;
  textAddToOrder: UntypedFormControl;
  progressBarColor: UntypedFormControl;
  showCompareAtPrice: UntypedFormControl;
  additionalDisplayTexts: UntypedFormArray;
  productReviews: UntypedFormGroup;
}

export interface FreeThresholdFormGroup {
  countriesAndRegions: UntypedFormControl;
  threshold: UntypedFormControl;
  currency: UntypedFormControl;
}

export interface ConfigureFormGroup {
  freeThresholds: UntypedFormArray;
}

export interface CheckoutUpsellFormGroup {
  usageToken: UntypedFormControl;
  internalName: UntypedFormControl;
  placementMode: UntypedFormControl;
  applyDiscountToRecommendedProducts: UntypedFormControl;
  discountConfiguration: UntypedFormGroup;
  title: UntypedFormControl;
  restrictionsRules: UntypedFormArray;
  whenConditionsNotMet: UntypedFormControl;
  style: UntypedFormGroup;
  settings: UntypedFormGroup;
  configure: UntypedFormGroup;
}

export function createGroupDiscountFormGroup(
  id: number,
  values: Partial<GroupDiscountConfigurationValues>,
  rulesDynamicFormService: RulesDynamicFormService,
): UntypedFormGroup {
  const discountApplyToRules = getExtensionRules({ onlyRules: PRODUCT_RELATED_RULE_ENTITIES });

  return new UntypedFormGroup({
    id: new UntypedFormControl(values.id ?? id),
    discountType: new UntypedFormControl(values.discountType ?? CheckoutUpsellExtension.DiscountType.FixedAmount),
    discountValue: new UntypedFormControl(values.discountValue ?? 0),
    labelText: new UntypedFormControl(values.labelText ?? ''),
    labelColor: new UntypedFormControl(values.labelColor ?? 'info'),
    applyTo: new UntypedFormGroup({
      mode: new UntypedFormControl(values.applyTo?.mode ?? 'all'),
      products: new UntypedFormControl(values.applyTo?.products ?? []),
      rules: rulesDynamicFormService.initRulesFormArray(values.applyTo?.rules?.flat() ?? [], discountApplyToRules),
    }),
  });
}

// showEmptyRowByDefault seeds a validator-free row — it'd otherwise save as an entity-less junk rule
export function removeDiscountRulesWithoutEntity(productSelection: ProductSelectionWithGroupDiscounts[]): void {
  productSelection?.forEach((ruleGroup) =>
    ruleGroup.discounts?.forEach((discount) => {
      if (discount.applyTo?.rules) {
        discount.applyTo.rules = discount.applyTo.rules.filter((rule) => !!rule?.entity);
      }
    }),
  );
}

export function createRuleGroupFormGroup(
  id: number,
  values: Partial<ProductSelectionWithGroupDiscounts>,
  rulesDynamicFormService: RulesDynamicFormService,
): UntypedFormGroup {
  const productsInCartRules = getExtensionRules();
  const upsellProductsRules = getExtensionRules({ onlyRules: PRODUCT_RELATED_RULE_ENTITIES });

  return new UntypedFormGroup(
    {
      id: new UntypedFormControl(values.id ?? id),
      productsInCart: new UntypedFormGroup({
        mode: new UntypedFormControl(values.productsInCart?.mode ?? CheckoutUpsellExtension.ProductsInCartMode.ANY),
        rules: rulesDynamicFormService.initRulesFormArray(
          values.productsInCart?.rules?.flat() ?? [],
          productsInCartRules,
        ),
      }),
      upsellRecommendedProducts: new UntypedFormGroup({
        mode: new UntypedFormControl(
          values.upsellRecommendedProducts?.mode ??
            CheckoutUpsellExtension.UpsellRecommendedProductsMode.SHOPIFY_RECOMMENDATIONS,
        ),
        products: new UntypedFormControl(values.upsellRecommendedProducts?.products ?? []),
        rules: rulesDynamicFormService.initRulesFormArray(
          values.upsellRecommendedProducts?.rules?.flat() ?? [],
          upsellProductsRules,
        ),
      }),
      inStockProductsOnly: new UntypedFormControl(values.inStockProductsOnly ?? true),
      matchFulfillmentCenterToCartItems: new UntypedFormControl(values.matchFulfillmentCenterToCartItems ?? false),
      includeDigitalItems: new UntypedFormControl(values.includeDigitalItems ?? true),
      matchProductVariants: new UntypedFormControl(values.matchProductVariants ?? false),
      hideProductsAlreadyInCart: new UntypedFormControl(values.hideProductsAlreadyInCart ?? 'parentProduct'),
      maxItemsToDisplay: new UntypedFormControl(values.maxItemsToDisplay ?? 100),
      NoItemsToDisplay: new UntypedFormControl(values.NoItemsToDisplay ?? 'showShopifyRecommendations'),
      prioritizeRecommendedItemsBy: new UntypedFormControl(
        values.prioritizeRecommendedItemsBy ?? CheckoutUpsellExtension.PrioritizeRecommendedItemsBy.HIGH_PRICE,
      ),
      sellingPlanId: new UntypedFormControl(values.sellingPlanId != null ? String(values.sellingPlanId) : ''),
      sellingPlanDescription: new UntypedFormControl(
        values.sellingPlanDescription ?? '<p>Today Only! $0! <s><b>{price}</b></s></p>',
      ),
      applyDiscountToRecommendedProducts: new UntypedFormControl(values.applyDiscountToRecommendedProducts ?? false),
      discounts: new UntypedFormArray(
        (values.discounts ?? []).map((discount, discountIndex) =>
          createGroupDiscountFormGroup(discount.id ?? discountIndex, discount, rulesDynamicFormService),
        ),
      ),
    },
    sellingPlanValidator,
  );
}

export interface ProductReviewsFormGroup {
  enabled: UntypedFormControl;
  importMethod: UntypedFormControl;
  reviewDataSource: UntypedFormControl;
  showStars: UntypedFormControl;
  showNumberOfReviews: UntypedFormControl;
  starColor: UntypedFormControl;
  position: UntypedFormControl;
  minStarRatingEnabled: UntypedFormControl;
  minStarRating: UntypedFormControl;
  minNumberOfReviewsEnabled: UntypedFormControl;
  minNumberOfReviews: UntypedFormControl;
  excludeProductsEnabled: UntypedFormControl;
  excludeProductsMode: UntypedFormControl;
  excludeProducts: UntypedFormControl;
  restrictionsRules: UntypedFormArray;
  csvFileUrl: UntypedFormControl;
  csvFileName: UntypedFormControl;
  csvFileSize: UntypedFormControl;
  reviewCount: UntypedFormControl;
  pendingCsvContent: UntypedFormControl;
}

export function createProductReviewsFormGroup(
  values: Partial<ProductReviewsConfig>,
  rulesDynamicFormService: RulesDynamicFormService,
): UntypedFormGroup {
  const excludeProductsRules = getExtensionRules({ onlyRules: PRODUCT_RELATED_RULE_ENTITIES });

  return new UntypedFormGroup({
    enabled: new UntypedFormControl(values?.enabled ?? false),
    importMethod: new UntypedFormControl(values?.importMethod ?? ProductReviews.ImportMethod.INTEGRATION),
    reviewDataSource: new UntypedFormControl(values?.reviewDataSource ?? null),
    showStars: new UntypedFormControl(values?.showStars ?? true),
    showNumberOfReviews: new UntypedFormControl(values?.showNumberOfReviews ?? true),
    starColor: new UntypedFormControl(values?.starColor ?? DynamicReviewsExtension.DEFAULT_STAR_COLOR),
    position: new UntypedFormControl(values?.position ?? ProductReviews.Position.MIDDLE),
    minStarRatingEnabled: new UntypedFormControl(values?.minStarRatingEnabled ?? false),
    minStarRating: new UntypedFormControl(values?.minStarRating ?? DynamicReviewsExtension.DEFAULT_MIN_STAR_RATING),
    minNumberOfReviewsEnabled: new UntypedFormControl(values?.minNumberOfReviewsEnabled ?? false),
    minNumberOfReviews: new UntypedFormControl(
      values?.minNumberOfReviews ?? ProductReviews.DEFAULT_MIN_NUMBER_OF_REVIEWS,
    ),
    excludeProductsEnabled: new UntypedFormControl(values?.excludeProductsEnabled ?? false),
    excludeProductsMode: new UntypedFormControl(
      values?.excludeProductsMode ?? CheckoutUpsellExtension.GroupDiscountApplyToMode.MANUAL,
    ),
    excludeProducts: new UntypedFormControl(values?.excludeProducts ?? []),
    restrictionsRules: rulesDynamicFormService.initRulesFormArray(
      values?.restrictionsRules?.flat() ?? [],
      excludeProductsRules,
    ),
    csvFileUrl: new UntypedFormControl(values?.csvFileUrl ?? ''),
    csvFileName: new UntypedFormControl(values?.csvFileName ?? ''),
    csvFileSize: new UntypedFormControl(values?.csvFileSize ?? 0),
    reviewCount: new UntypedFormControl(values?.reviewCount ?? 0),
    pendingCsvContent: new UntypedFormControl(values?.pendingCsvContent ?? ''),
  });
}
